import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';

import {
  assignWorkspacePackage,
  createWorkspace,
  fetchWorkspacePackageCandidates,
  fetchWorkspacePackages,
  fetchWorkspaces,
  importWorkspaceManifest,
  recheckWorkspacePackages,
  submitIndexRequest,
  updateWorkspacePackageVersion,
} from '../api';
import { useOrg } from '../OrgProvider';
import { PanelHeader } from './PanelHeader';
import { relativeTime } from '../utils';
import type {
  Workspace,
  WorkspaceImportResult,
  WorkspacePackage,
  WorkspacePackageCandidate,
  WorkspaceResolutionStatus,
} from '../types';

type StatusFilter = WorkspaceResolutionStatus | 'all';

const STATUS_ORDER: WorkspaceResolutionStatus[] = [
  'exact',
  'range-match',
  'out-of-range',
  'missing',
];

const STATUS_LABELS: Record<WorkspaceResolutionStatus, string> = {
  exact: 'Exact',
  'range-match': 'Range-match',
  'out-of-range': 'Out-of-range',
  missing: 'Missing',
};

const STATUS_PILL_CLASSES: Record<WorkspaceResolutionStatus, string> = {
  exact: 'bg-good/15 text-good',
  'range-match': 'bg-warn/15 text-warn',
  'out-of-range': 'bg-warn/15 text-warn',
  missing: 'bg-bg text-dim',
};

function statusLabel(status: WorkspaceResolutionStatus | null): string {
  return status ? STATUS_LABELS[status] : 'Unresolved';
}

function pkgStatus(pkg: WorkspacePackage): WorkspaceResolutionStatus {
  if (pkg.resolution_status) return pkg.resolution_status;
  // Legacy rows written before migration 023 — infer from the resolved fields.
  return pkg.resolved_version ? 'exact' : 'missing';
}

const WORKSPACE_NAME_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function normalizeWorkspaceNameInput(value: string): string {
  return value
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '');
}

function getRequestableIndexVersion(versionSpec: string | null): string | undefined {
  const normalized = versionSpec?.trim();
  if (!normalized) return undefined;
  const exactMatch = normalized.match(/^(?:=|==|===)?\s*([0-9A-Za-z][0-9A-Za-z.+-]*)$/);
  return exactMatch?.[1];
}

interface ResolveResult {
  version: string;
}

async function resolvePackageLatest(
  registry: 'npm' | 'pub' | 'pypi',
  name: string
): Promise<ResolveResult> {
  if (registry === 'npm') {
    const encodedName = name.startsWith('@') ? name.replace('/', '%2F') : name;
    const response = await fetch(`https://registry.npmjs.org/${encodedName}/latest`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`npm registry returned ${response.status} for "${name}"`);
    const data = (await response.json()) as { version: string };
    return { version: data.version };
  }

  if (registry === 'pypi') {
    const response = await fetch(`https://pypi.org/pypi/${encodeURIComponent(name)}/json`);
    if (!response.ok) throw new Error(`PyPI returned ${response.status} for "${name}"`);
    const data = (await response.json()) as { info: { version: string } };
    return { version: data.info.version };
  }

  if (registry === 'pub') {
    const response = await fetch(`https://pub.dev/api/packages/${encodeURIComponent(name)}`);
    if (!response.ok) throw new Error(`pub.dev returned ${response.status} for "${name}"`);
    const data = (await response.json()) as { latest: { version: string } };
    return { version: data.latest.version };
  }

  throw new Error(`Unknown registry: ${registry}`);
}

function FilePickerField({
  id,
  label,
  hint,
  accept,
  fileName,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  accept: string;
  fileName: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium uppercase tracking-wider text-dim">{label}</label>
      <p className="text-xs text-dim">{hint}</p>
      <div className="flex items-center gap-2">
        <label
          htmlFor={id}
          className="inline-flex cursor-pointer items-center justify-center border border-rule bg-bg px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:border-accent hover:text-accent"
        >
          Choose file
        </label>
        <input id={id} type="file" accept={accept} onChange={onChange} className="sr-only" />
        <span className="truncate text-xs text-dim">{fileName || 'No file chosen'}</span>
      </div>
    </div>
  );
}

function AddWorkspaceDialog({
  open,
  isSubmitting,
  name,
  description,
  manifestName,
  lockFileName,
  error,
  onClose,
  onNameChange,
  onDescriptionChange,
  onManifestChange,
  onLockFileChange,
  onSubmit,
}: {
  open: boolean;
  isSubmitting: boolean;
  name: string;
  description: string;
  manifestName: string;
  lockFileName: string;
  error: string | null;
  onClose: () => void;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onManifestChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onLockFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg overflow-hidden border border-rule bg-panel shadow-2xl">
        <div className="flex items-start justify-between border-b border-rule-soft px-6 py-5">
          <div>
            <p className="text-2xs font-medium uppercase tracking-[0.18em] text-dim">Workspace</p>
            <h3 className="mt-1 text-lg font-semibold text-ink">Add workspace</h3>
            <p className="mt-1 text-sm text-dim">
              Create a workspace and optionally import a project config file in the same flow.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-dim transition-colors hover:bg-bg hover:text-link"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-dim">
              Workspace name
            </label>
            <input
              value={name}
              onChange={(event) => onNameChange(event.target.value)}
              placeholder="frontend-platform"
              className="lc-input w-full"
            />
            <p className="text-xs text-dim">
              Lowercase letters, numbers, and hyphens only. This name is the workspace selector used
              in REST and MCP.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-medium uppercase tracking-wider text-dim">
              Description
            </label>
            <textarea
              value={description}
              onChange={(event) => onDescriptionChange(event.target.value)}
              placeholder="Optional description"
              rows={3}
              className="lc-input w-full"
            />
          </div>

          <div className="space-y-3 border border-dashed border-rule p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-dim">
              Project files <span className="normal-case font-normal">(optional)</span>
            </p>
            <FilePickerField
              id="workspace-manifest"
              label="Manifest"
              hint="package.json, pyproject.toml, Cargo.toml, pom.xml, Package.resolved, etc."
              accept=".json,.yaml,.yml,.toml,.xml,.resolved"
              fileName={manifestName}
              onChange={onManifestChange}
            />
            <FilePickerField
              id="workspace-lockfile"
              label="Lockfile"
              hint="package-lock.json, yarn.lock, pnpm-lock.yaml, poetry.lock, pubspec.lock, Cargo.lock"
              accept=".json,.lock,.txt,.yaml,.yml"
              fileName={lockFileName}
              onChange={onLockFileChange}
            />
          </div>

          {error ? (
            <div className="border border-del/30 bg-del/15 px-3 py-2 text-sm text-del">{error}</div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-rule-soft px-6 py-4">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSubmit}
            disabled={isSubmitting || !WORKSPACE_NAME_PATTERN.test(name.trim())}
          >
            {isSubmitting ? 'Saving…' : 'Create workspace'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Defensive guard: workspaces are a SaaS-only feature. Even if a future
// caller mounts this without gating, render nothing on self-host so we
// never call workspace APIs that would 404. The actual panel is split into
// an inner component so we can early-return before any hooks are called.
export function WorkspacesPanel() {
  const { deploymentMode } = useAuth();
  if (deploymentMode !== 'saas') return null;
  return <WorkspacesPanelInner />;
}

function WorkspacesPanelInner() {
  const { activeOrg } = useOrg();
  const { getToken } = useAuth();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedWorkspaceSlug, setSelectedWorkspaceSlug] = useState<string | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [workspacePackages, setWorkspacePackages] = useState<WorkspacePackage[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [manifestName, setManifestName] = useState('');
  const [manifestContent, setManifestContent] = useState('');
  const [lockFileName, setLockFileName] = useState('');
  const [lockContent, setLockContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<string | null>(null);
  const [editingVersionSpec, setEditingVersionSpec] = useState('');
  const [savingPackageId, setSavingPackageId] = useState<string | null>(null);
  const [requestingPackageId, setRequestingPackageId] = useState<string | null>(null);
  const [requestedPackageIds, setRequestedPackageIds] = useState<Set<string>>(new Set());
  const [isRequestingAll, setIsRequestingAll] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const [assigningPackageId, setAssigningPackageId] = useState<string | null>(null);
  const [assignCandidates, setAssignCandidates] = useState<WorkspacePackageCandidate[]>([]);
  const [isFetchingCandidates, setIsFetchingCandidates] = useState(false);
  const [packageActionMessage, setPackageActionMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<WorkspaceImportResult | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    const load = async () => {
      const token = await getToken();
      if (!token || !activeOrg) {
        setWorkspaces([]);
        setSelectedWorkspaceSlug(null);
        setWorkspacePackages([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const nextWorkspaces = await fetchWorkspaces(activeOrg.id, token);
        setWorkspaces(nextWorkspaces);
        setSelectedWorkspaceSlug((current) =>
          current && nextWorkspaces.some((workspace) => workspace.slug === current) ? current : null
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspaces.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [activeOrg, getToken]);

  useEffect(() => {
    const loadPackages = async () => {
      const token = await getToken();
      if (!token || !activeOrg || !selectedWorkspaceSlug) {
        setWorkspacePackages([]);
        return;
      }

      try {
        const packages = await fetchWorkspacePackages(activeOrg.id, selectedWorkspaceSlug, token);
        setWorkspacePackages(packages);
        setRequestedPackageIds(
          (current) =>
            new Set(
              [...current].filter((packageId) => packages.some((pkg) => pkg.id === packageId))
            )
        );
        setPackageActionMessage(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspace packages.');
      }
    };

    void loadPackages();
  }, [activeOrg, getToken, selectedWorkspaceSlug]);

  const selectedWorkspace = selectedWorkspaceSlug
    ? (workspaces.find((workspace) => workspace.slug === selectedWorkspaceSlug) ?? null)
    : null;

  const statusCounts = workspacePackages.reduce<Record<WorkspaceResolutionStatus, number>>(
    (acc, pkg) => {
      acc[pkgStatus(pkg)] += 1;
      return acc;
    },
    { exact: 0, 'range-match': 0, 'out-of-range': 0, missing: 0 }
  );
  const hasImportedPackages = workspacePackages.length > 0;
  const filteredPackages =
    statusFilter === 'all'
      ? workspacePackages
      : workspacePackages.filter((pkg) => pkgStatus(pkg) === statusFilter);

  const handleCreateWorkspace = async () => {
    const normalizedName = normalizeWorkspaceNameInput(createName.trim()).replace(/-+$/g, '');
    if (!activeOrg || !WORKSPACE_NAME_PATTERN.test(normalizedName)) {
      setError('Workspace names may only use lowercase letters, numbers, and hyphens.');
      return;
    }
    const token = await getToken();
    if (!token) return;

    setIsCreating(true);
    setError(null);
    try {
      const workspace = await createWorkspace(
        activeOrg.id,
        {
          name: normalizedName,
          slug: normalizedName,
          description: createDescription.trim() || undefined,
        },
        token
      );

      if (manifestName.trim() && manifestContent.trim()) {
        await importWorkspaceManifest(
          activeOrg.id,
          workspace.slug,
          {
            file_name: manifestName.trim(),
            raw_content: manifestContent,
            ...(lockFileName.trim() && lockContent
              ? { lock_file_name: lockFileName.trim(), lock_raw_content: lockContent }
              : {}),
          },
          token
        );
      }

      setWorkspaces((current) => [workspace, ...current]);
      setSelectedWorkspaceSlug(workspace.slug);
      setIsDetailsOpen(false);
      setIsAddDialogOpen(false);
      setCreateName('');
      setCreateDescription('');
      setManifestName('');
      setManifestContent('');
      setLockFileName('');
      setLockContent('');
      setRequestedPackageIds(new Set());
      setPackageActionMessage(null);
      setImportResult(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workspace.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleManifestFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setManifestName(file.name);
    setManifestContent(await file.text());
  };

  const handleLockFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setLockFileName('');
      setLockContent('');
      return;
    }
    setLockFileName(file.name);
    setLockContent(await file.text());
  };

  const handleImportManifest = async () => {
    if (!activeOrg || !selectedWorkspace || !manifestName.trim() || !manifestContent.trim()) return;
    const token = await getToken();
    if (!token) return;

    if (hasImportedPackages) {
      const confirmed = window.confirm(
        'Re-importing will replace the existing pinned packages for this workspace manifest. Continue?'
      );
      if (!confirmed) return;
    }

    setIsImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await importWorkspaceManifest(
        activeOrg.id,
        selectedWorkspace.slug,
        {
          file_name: manifestName.trim(),
          raw_content: manifestContent,
          ...(lockFileName.trim() && lockContent
            ? { lock_file_name: lockFileName.trim(), lock_raw_content: lockContent }
            : {}),
        },
        token
      );
      setImportResult(result);
      const packages = await fetchWorkspacePackages(activeOrg.id, selectedWorkspace.slug, token);
      setWorkspacePackages(packages);
      setRequestedPackageIds(new Set());
      setPackageActionMessage(null);
      setManifestName('');
      setManifestContent('');
      setLockFileName('');
      setLockContent('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import manifest.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleSelectWorkspace = (workspaceSlug: string) => {
    setSelectedWorkspaceSlug(workspaceSlug);
    setIsDetailsOpen(false);
    setManifestName('');
    setManifestContent('');
    setLockFileName('');
    setLockContent('');
    setImportResult(null);
    setPackageActionMessage(null);
    setEditingPackageId(null);
    setEditingVersionSpec('');
    setRequestedPackageIds(new Set());
    setError(null);
  };

  const toggleDetails = () => {
    if (!selectedWorkspaceSlug) return;
    setIsDetailsOpen((current) => !current);
  };

  const closeAddDialog = () => {
    setIsAddDialogOpen(false);
    setCreateName('');
    setCreateDescription('');
    setManifestName('');
    setManifestContent('');
    setPackageActionMessage(null);
    setError(null);
  };

  const copyText = (field: string, value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField((current) => (current === field ? null : current)), 2000);
  };

  const handleStartEditPackage = (pkg: WorkspacePackage) => {
    setEditingPackageId(pkg.id);
    setEditingVersionSpec(pkg.version_spec ?? '');
    setPackageActionMessage(null);
    setError(null);
  };

  const handleCancelEditPackage = () => {
    setEditingPackageId(null);
    setEditingVersionSpec('');
  };

  const handleSavePackageVersion = async (pkg: WorkspacePackage) => {
    if (!activeOrg || !selectedWorkspace) return;
    const token = await getToken();
    if (!token) return;

    setSavingPackageId(pkg.id);
    setError(null);
    setPackageActionMessage(null);
    try {
      const updated = await updateWorkspacePackageVersion(
        activeOrg.id,
        selectedWorkspace.slug,
        pkg.id,
        { version_spec: editingVersionSpec.trim() || null },
        token
      );
      setWorkspacePackages((current) =>
        current.map((entry) => (entry.id === updated.id ? updated : entry))
      );
      setRequestedPackageIds((current) => {
        const next = new Set(current);
        next.delete(updated.id);
        return next;
      });
      setEditingPackageId(null);
      setEditingVersionSpec('');
      setPackageActionMessage(
        `Updated ${updated.package_name} to ${updated.version_spec ?? 'latest'} and re-ran resolution.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update package version.');
    } finally {
      setSavingPackageId(null);
    }
  };

  const handleOpenAssign = async (pkg: WorkspacePackage) => {
    const token = await getToken();
    if (!token || !activeOrg || !selectedWorkspace) return;
    setAssigningPackageId(pkg.id);
    setAssignCandidates([]);
    setIsFetchingCandidates(true);
    try {
      const candidates = await fetchWorkspacePackageCandidates(
        activeOrg.id,
        selectedWorkspace.slug,
        pkg.id,
        token
      );
      setAssignCandidates(candidates);
    } catch {
      setAssignCandidates([]);
    } finally {
      setIsFetchingCandidates(false);
    }
  };

  const handleAssign = async (pkg: WorkspacePackage, packageId: string | null) => {
    const token = await getToken();
    if (!token || !activeOrg || !selectedWorkspace) return;
    setError(null);
    setPackageActionMessage(null);
    try {
      const updated = await assignWorkspacePackage(
        activeOrg.id,
        selectedWorkspace.slug,
        pkg.id,
        packageId,
        token
      );
      setWorkspacePackages((current) => current.map((p) => (p.id === updated.id ? updated : p)));
      setAssigningPackageId(null);
      setPackageActionMessage(
        packageId
          ? `Assigned ${pkg.package_name} → ${updated.resolved_version ?? packageId}.`
          : `Cleared assignment for ${pkg.package_name}.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign package.');
    }
  };

  const handleRecheck = async () => {
    const token = await getToken();
    if (!token || !activeOrg || !selectedWorkspace) return;
    setIsRechecking(true);
    setError(null);
    setPackageActionMessage(null);
    try {
      const result = await recheckWorkspacePackages(activeOrg.id, selectedWorkspace.slug, token);
      const packages = await fetchWorkspacePackages(activeOrg.id, selectedWorkspace.slug, token);
      setWorkspacePackages(packages);
      setPackageActionMessage(
        `Rechecked ${result.totalCount} packages — ${result.resolvedCount} resolved.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recheck workspace packages.');
    } finally {
      setIsRechecking(false);
    }
  };

  const handleRequestAllUnresolved = async () => {
    const unresolved = workspacePackages.filter((pkg) => {
      const status = pkgStatus(pkg);
      return (
        (status === 'missing' || status === 'out-of-range') && !requestedPackageIds.has(pkg.id)
      );
    });
    if (unresolved.length === 0) return;
    setIsRequestingAll(true);
    setError(null);
    setPackageActionMessage(null);
    let successCount = 0;
    for (const pkg of unresolved) {
      try {
        const knownVersion = getRequestableIndexVersion(pkg.version_spec);
        const version =
          knownVersion ??
          (await resolvePackageLatest(pkg.registry, pkg.package_name)
            .then((r) => r.version)
            .catch(() => undefined));
        if (!version) continue;
        await submitIndexRequest({ registry: pkg.registry, name: pkg.package_name, version });
        setRequestedPackageIds((current) => new Set(current).add(pkg.id));
        successCount++;
      } catch {
        // continue with remaining packages
      }
    }
    setIsRequestingAll(false);
    setPackageActionMessage(
      `Requested indexing for ${successCount} of ${unresolved.length} unresolved package${unresolved.length !== 1 ? 's' : ''}.`
    );
  };

  const handleRequestPackageIndex = async (pkg: WorkspacePackage) => {
    const knownVersion = getRequestableIndexVersion(pkg.version_spec);
    const version =
      knownVersion ??
      (await resolvePackageLatest(pkg.registry, pkg.package_name)
        .then((r) => r.version)
        .catch(() => undefined));
    if (!version) {
      setError(`Could not determine a version for ${pkg.package_name}.`);
      return;
    }
    setRequestingPackageId(pkg.id);
    setError(null);
    setPackageActionMessage(null);
    try {
      await submitIndexRequest({ registry: pkg.registry, name: pkg.package_name, version });
      setRequestedPackageIds((current) => new Set(current).add(pkg.id));
      setPackageActionMessage(`Requested indexing for ${pkg.package_name}@${version}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request package indexing.');
    } finally {
      setRequestingPackageId(null);
    }
  };

  return (
    <section className="space-y-4">
      <AddWorkspaceDialog
        open={isAddDialogOpen}
        isSubmitting={isCreating}
        name={createName}
        description={createDescription}
        manifestName={manifestName}
        lockFileName={lockFileName}
        error={error}
        onClose={closeAddDialog}
        onNameChange={(value) => {
          setCreateName(normalizeWorkspaceNameInput(value));
          if (error) setError(null);
        }}
        onDescriptionChange={setCreateDescription}
        onManifestChange={handleManifestFile}
        onLockFileChange={handleLockFile}
        onSubmit={() => void handleCreateWorkspace()}
      />

      <PanelHeader
        title="Workspaces"
        count={workspaces.length}
        description="Import a manifest and pin retrieval to the packages resolved for that workspace."
        icon={
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.6}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z"
            />
          </svg>
        }
        action={
          <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
            Add workspace
          </Button>
        }
      />

      <div className="space-y-4">
        <div className="lc-surface space-y-3 px-4 py-4">
          {isLoading ? (
            <p className="text-sm text-dim">Loading…</p>
          ) : workspaces.length === 0 ? (
            <div className="border border-dashed border-rule px-4 py-6 text-sm text-dim">
              No workspaces yet. Use the add button to create one and optionally import a project
              config file.
            </div>
          ) : (
            <div className="space-y-3">
              {workspaces.map((workspace) => {
                const isSelected = workspace.slug === selectedWorkspaceSlug;
                const isOpen = isSelected && isDetailsOpen;

                return (
                  <div key={workspace.id} className="sheet px-5 py-5 transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h5 className="text-sm font-semibold text-ink">{workspace.name}</h5>
                          <span className="chip text-3xs uppercase tracking-wider">
                            {workspace.slug}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-dim">
                          {workspace.description || 'No description yet.'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          if (!isSelected) {
                            handleSelectWorkspace(workspace.slug);
                            setIsDetailsOpen(true);
                            return;
                          }
                          toggleDetails();
                        }}
                      >
                        {isOpen ? 'Hide details' : 'View details'}
                      </Button>
                    </div>

                    {isSelected ? (
                      <div className="mt-5 space-y-5">
                        {/* Meta — created / manifest imported / last checked */}
                        {(() => {
                          const manifestAt = workspacePackages.reduce<string | null>((acc, p) => {
                            if (!p.created_at) return acc;
                            return !acc || p.created_at > acc ? p.created_at : acc;
                          }, null);
                          const checkedAt = workspacePackages.reduce<string | null>((acc, p) => {
                            if (!p.updated_at) return acc;
                            return !acc || p.updated_at > acc ? p.updated_at : acc;
                          }, null);
                          return (
                            <div className="flex flex-wrap gap-x-5 gap-y-1 text-2xs text-dim">
                              <span>
                                Created{' '}
                                <span
                                  className="text-link"
                                  title={new Date(workspace.created_at).toLocaleString()}
                                >
                                  {relativeTime(workspace.created_at)}
                                </span>
                              </span>
                              <span>
                                Manifest imported{' '}
                                <span
                                  className="text-link"
                                  title={manifestAt ? new Date(manifestAt).toLocaleString() : ''}
                                >
                                  {manifestAt ? relativeTime(manifestAt) : '—'}
                                </span>
                              </span>
                              <span>
                                Last resolved{' '}
                                <span
                                  className="text-link"
                                  title={checkedAt ? new Date(checkedAt).toLocaleString() : ''}
                                >
                                  {checkedAt ? relativeTime(checkedAt) : '—'}
                                </span>
                              </span>
                            </div>
                          );
                        })()}

                        {/* Workspace selector value — single compact strip */}
                        <div className="flex flex-wrap items-center gap-3 bg-bg px-3 py-2.5">
                          <span className="text-2xs font-medium uppercase tracking-[0.16em] text-dim">
                            Selector
                          </span>
                          <code className="font-mono text-xs text-link">{`workspace: "${workspace.slug}"`}</code>
                          <button
                            type="button"
                            onClick={() => copyText(`workspace-${workspace.slug}`, workspace.slug)}
                            className="ml-auto rounded px-2 py-0.5 text-2xs font-medium text-dim transition-colors hover:bg-panel hover:text-link"
                          >
                            {copiedField === `workspace-${workspace.slug}` ? 'Copied' : 'Copy'}
                          </button>
                          <span className="basis-full text-2xs text-dim">
                            Pass this in the REST body for{' '}
                            <code className="rounded border border-accent/40 bg-accent-soft px-1.5 py-[1px] font-mono text-2xs text-accent">
                              /api/context
                            </code>
                            , or as an MCP tool argument to{' '}
                            <code className="rounded border border-accent/40 bg-accent-soft px-1.5 py-[1px] font-mono text-2xs text-accent">
                              get_context
                            </code>
                            .
                          </span>
                        </div>
                      </div>
                    ) : null}

                    {isOpen ? (
                      <div className="mt-5 space-y-5 border-t border-rule pt-5">
                        <div className="space-y-2.5 bg-bg px-3 py-2.5">
                          <div className="flex items-center justify-between gap-3">
                            <label className="text-2xs font-medium uppercase tracking-[0.16em] text-dim">
                              {hasImportedPackages ? 'Re-import manifest' : 'Import manifest'}
                            </label>
                            <Button
                              size="sm"
                              onClick={() => void handleImportManifest()}
                              disabled={
                                isImporting ||
                                !selectedWorkspace ||
                                !manifestName.trim() ||
                                !manifestContent.trim()
                              }
                            >
                              {isImporting
                                ? 'Importing…'
                                : hasImportedPackages
                                  ? 'Re-import'
                                  : 'Import'}
                            </Button>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <label className="inline-flex cursor-pointer items-center gap-2">
                              <span className="chip bg-panel px-2.5 py-1 font-medium text-link transition-colors hover:bg-bg">
                                Manifest
                              </span>
                              <span className="truncate text-dim">
                                {manifestName || 'No file selected'}
                              </span>
                              <input
                                type="file"
                                accept=".json,.yaml,.yml,.toml"
                                className="sr-only"
                                onChange={(event) => void handleManifestFile(event)}
                              />
                            </label>
                            <label className="inline-flex cursor-pointer items-center gap-2">
                              <span className="chip bg-panel px-2.5 py-1 font-medium text-link transition-colors hover:bg-bg">
                                Lock file
                              </span>
                              <span className="truncate text-dim">
                                {lockFileName || 'Optional'}
                              </span>
                              <input
                                type="file"
                                accept=".json,.yaml,.yml,.lock"
                                className="sr-only"
                                onChange={(event) => void handleLockFile(event)}
                              />
                            </label>
                          </div>
                          {lockFileName ? (
                            <p className="text-2xs text-good">
                              Exact versions from the lockfile will be used.
                            </p>
                          ) : (
                            <p className="text-2xs text-dim">
                              Supports package-lock.json, yarn.lock, pnpm-lock.yaml, poetry.lock,
                              pubspec.lock, Cargo.lock.
                            </p>
                          )}
                          {importResult ? (
                            <p className="text-2xs text-good">
                              Imported {importResult.dependencyCount} dependencies from{' '}
                              {importResult.manifestType} · resolved {importResult.resolvedCount}.
                            </p>
                          ) : null}
                        </div>

                        {error ? (
                          <div className="border border-del/30 bg-del/15 px-3 py-2 text-sm text-del">
                            {error}
                          </div>
                        ) : null}

                        {packageActionMessage ? (
                          <div className="border border-good/30 bg-good/15 px-3 py-2 text-sm text-good">
                            {packageActionMessage}
                          </div>
                        ) : null}

                        <div className="border-t border-rule pt-5">
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <h5 className="text-2xs font-medium uppercase tracking-[0.14em] text-dim">
                              Pinned packages
                            </h5>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => void handleRecheck()}
                                disabled={isRechecking || workspacePackages.length === 0}
                                className="rounded px-2 py-1 text-xs font-medium text-dim transition-colors hover:bg-bg hover:text-link disabled:opacity-50"
                              >
                                {isRechecking ? 'Rechecking…' : 'Recheck'}
                              </button>
                              {workspacePackages.some((pkg) => {
                                const s = pkgStatus(pkg);
                                return (
                                  (s === 'missing' || s === 'out-of-range') &&
                                  !requestedPackageIds.has(pkg.id)
                                );
                              }) ? (
                                <button
                                  type="button"
                                  onClick={() => void handleRequestAllUnresolved()}
                                  disabled={isRequestingAll}
                                  className="rounded px-2 py-1 text-xs font-medium text-accent transition-colors hover:bg-accent-soft disabled:opacity-50"
                                >
                                  {isRequestingAll ? 'Requesting…' : 'Request all unresolved'}
                                </button>
                              ) : null}
                              <span className="pl-2 text-xs text-dim">
                                {statusFilter === 'all'
                                  ? workspacePackages.length
                                  : `${filteredPackages.length} / ${workspacePackages.length}`}
                              </span>
                            </div>
                          </div>
                          {hasImportedPackages ? (
                            <div className="mb-5 flex flex-wrap items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => setStatusFilter('all')}
                                className={`px-3.5 py-1.5 text-xs font-medium transition-colors ${
                                  statusFilter === 'all'
                                    ? 'bg-accent-soft text-accent'
                                    : 'bg-bg text-dim hover:bg-accent-soft'
                                }`}
                              >
                                All ({workspacePackages.length})
                              </button>
                              {STATUS_ORDER.map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  onClick={() =>
                                    setStatusFilter(statusFilter === status ? 'all' : status)
                                  }
                                  disabled={statusCounts[status] === 0}
                                  className={`px-3.5 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                                    statusFilter === status
                                      ? STATUS_PILL_CLASSES[status] + ' ring-1 ring-current'
                                      : STATUS_PILL_CLASSES[status]
                                  }`}
                                >
                                  {STATUS_LABELS[status]} ({statusCounts[status]})
                                </button>
                              ))}
                            </div>
                          ) : null}
                          {workspacePackages.length === 0 ? (
                            <div className="border border-rule px-4 py-6 text-sm text-dim">
                              No manifest imported for this workspace yet.
                            </div>
                          ) : (
                            <div className="overflow-hidden border border-rule">
                              <div className="max-h-[24rem] overflow-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-rule-soft bg-bg">
                                      <th className="px-3 py-2 text-left text-2xs font-medium uppercase tracking-wider text-dim">
                                        Package
                                      </th>
                                      <th className="px-3 py-2 text-left text-2xs font-medium uppercase tracking-wider text-dim">
                                        Spec
                                      </th>
                                      <th className="px-3 py-2 text-left text-2xs font-medium uppercase tracking-wider text-dim">
                                        Resolved
                                      </th>
                                      <th className="px-3 py-2 text-left text-2xs font-medium uppercase tracking-wider text-dim">
                                        Source
                                      </th>
                                      <th className="px-3 py-2 text-left text-2xs font-medium uppercase tracking-wider text-dim">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-rule-soft">
                                    {filteredPackages.map((pkg) => {
                                      const isEditing = editingPackageId === pkg.id;
                                      const isRequested = requestedPackageIds.has(pkg.id);
                                      const requestVersion = getRequestableIndexVersion(
                                        pkg.version_spec
                                      );
                                      const status = pkgStatus(pkg);
                                      const alias = pkg.resolved_via_alias;
                                      const lockDiffers =
                                        !!pkg.lockfile_version &&
                                        pkg.lockfile_version !== pkg.version_spec;
                                      const reason = pkg.resolution_reason ?? '';

                                      return (
                                        <tr key={pkg.id}>
                                          <td className="px-3 py-2 font-medium text-ink">
                                            {pkg.package_name}
                                          </td>
                                          <td className="px-3 py-2 font-mono text-xs text-dim">
                                            {isEditing ? (
                                              <input
                                                value={editingVersionSpec}
                                                onChange={(event) =>
                                                  setEditingVersionSpec(event.target.value)
                                                }
                                                placeholder="latest"
                                                className="lc-input h-8 min-w-[10rem] font-mono text-xs"
                                              />
                                            ) : (
                                              <div className="flex flex-col">
                                                <span>{pkg.version_spec ?? '—'}</span>
                                                {lockDiffers ? (
                                                  <span className="text-3xs text-dim">
                                                    lock: {pkg.lockfile_version}
                                                  </span>
                                                ) : null}
                                              </div>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-xs">
                                            <div
                                              className="flex flex-wrap items-center gap-1"
                                              title={reason}
                                            >
                                              {status === 'missing' ? (
                                                <span
                                                  className={`inline-flex px-2 py-0.5 font-medium ${STATUS_PILL_CLASSES.missing}`}
                                                >
                                                  missing
                                                </span>
                                              ) : (
                                                <span
                                                  className={`inline-flex px-2 py-0.5 font-medium ${STATUS_PILL_CLASSES[status]}`}
                                                >
                                                  {pkg.resolved_version ?? '—'}
                                                </span>
                                              )}
                                              {status !== 'exact' && status !== 'missing' ? (
                                                <span className="text-3xs uppercase tracking-wider text-dim">
                                                  {statusLabel(status)}
                                                </span>
                                              ) : null}
                                              {alias ? (
                                                <span
                                                  className="chip-accent text-3xs"
                                                  title={`via ${alias.registry}:${alias.name}${alias.status ? ` (${alias.status})` : ''}${alias.verification_method ? ` · ${alias.verification_method}` : ''}`}
                                                >
                                                  via {alias.registry}:{alias.name}
                                                </span>
                                              ) : null}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 text-xs text-dim">
                                            {pkg.manifest_type} · {pkg.section}
                                            {pkg.group_name ? `/${pkg.group_name}` : ''}
                                          </td>
                                          <td className="px-3 py-2 text-xs text-dim">
                                            <div className="flex flex-wrap items-center gap-2">
                                              {isEditing ? (
                                                <>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      void handleSavePackageVersion(pkg)
                                                    }
                                                    disabled={savingPackageId === pkg.id}
                                                    className="rounded px-2 py-1 font-medium text-accent transition-colors hover:bg-accent-soft disabled:opacity-50"
                                                  >
                                                    {savingPackageId === pkg.id
                                                      ? 'Saving…'
                                                      : 'Save'}
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={handleCancelEditPackage}
                                                    disabled={savingPackageId === pkg.id}
                                                    className="rounded px-2 py-1 font-medium transition-colors hover:bg-bg hover:text-link disabled:opacity-50"
                                                  >
                                                    Cancel
                                                  </button>
                                                </>
                                              ) : (
                                                <>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleStartEditPackage(pkg)}
                                                    className="rounded px-2 py-1 font-medium transition-colors hover:bg-bg hover:text-link"
                                                  >
                                                    Edit version
                                                  </button>
                                                  {status === 'missing' ||
                                                  status === 'out-of-range' ? (
                                                    <button
                                                      type="button"
                                                      onClick={() =>
                                                        void handleRequestPackageIndex(pkg)
                                                      }
                                                      disabled={
                                                        requestingPackageId === pkg.id ||
                                                        isRequested
                                                      }
                                                      className="rounded px-2 py-1 font-medium text-accent transition-colors hover:bg-accent-soft disabled:opacity-50"
                                                    >
                                                      {requestingPackageId === pkg.id
                                                        ? 'Requesting…'
                                                        : isRequested
                                                          ? 'Requested'
                                                          : requestVersion
                                                            ? 'Request index'
                                                            : 'Request latest'}
                                                    </button>
                                                  ) : null}
                                                  {assigningPackageId === pkg.id ? (
                                                    <div className="mt-1 w-full space-y-1">
                                                      {isFetchingCandidates ? (
                                                        <span className="text-xs text-dim">
                                                          Loading candidates…
                                                        </span>
                                                      ) : assignCandidates.length === 0 ? (
                                                        <span className="text-xs text-dim">
                                                          No indexed packages found.
                                                        </span>
                                                      ) : (
                                                        <select
                                                          className="lc-input h-8 w-full text-xs"
                                                          defaultValue=""
                                                          onChange={(e) => {
                                                            if (e.target.value)
                                                              void handleAssign(
                                                                pkg,
                                                                e.target.value
                                                              );
                                                          }}
                                                        >
                                                          <option value="" disabled>
                                                            Pick a package…
                                                          </option>
                                                          {assignCandidates.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                              {c.name}@{c.version}
                                                              {c.org_id ? ' (org)' : ' (public)'}
                                                            </option>
                                                          ))}
                                                        </select>
                                                      )}
                                                      <button
                                                        type="button"
                                                        onClick={() => setAssigningPackageId(null)}
                                                        className="text-xs text-dim hover:text-link"
                                                      >
                                                        Cancel
                                                      </button>
                                                    </div>
                                                  ) : (
                                                    <button
                                                      type="button"
                                                      onClick={() => void handleOpenAssign(pkg)}
                                                      className="rounded px-2 py-1 font-medium text-dim transition-colors hover:bg-bg hover:text-link"
                                                    >
                                                      Assign
                                                    </button>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
